// Reject confirmation modal — sibling of ApproveVariationDialog. Per
// PO feedback the summary block also surfaces Value impact (managers
// rejecting a variation should see the financial magnitude at the
// moment of decision, not just the name + effective date). Carries
// an optional reason textarea (≤500 chars) that the backend persists
// on the variation as `rejectedReason` for audit. Same 409 fixed-copy
// surface as the approve dialog so concurrent decisions read
// identically regardless of which CCFE branch fired.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import { isConflictError } from "../../../../services/api";
import type { Sow, Variation } from "../../../../types";
import VariesCell from "./VariesCell";
import { readTargets } from "./variationTargets";
import ValueImpactCell from "./ValueImpactCell";

const ALREADY_DECIDED_COPY =
  "This variation has already been approved or rejected. Refresh the page to see the latest status.";

const MAX_REASON = 500;

interface RejectVariationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  variation: Variation | null;
  sowsById: Map<string, Sow>;
  /** Drives the GST basis suffix on the Value impact row in the
   *  summary block. Required because every monetary surface on a
   *  contract page must label its basis. */
  gstApplies: boolean | undefined;
  onConfirm: (reason?: string) => Promise<unknown>;
  isSubmitting?: boolean;
}

export default function RejectVariationDialog({
  isOpen,
  onClose,
  variation,
  sowsById,
  gstApplies,
  onConfirm,
  isSubmitting,
}: RejectVariationDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Pattern 0: clear reason and error when the dialog opens so a previous
  // entry does not persist across invocations.
  const seedKey = isOpen ? (variation?.id ?? "open") : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setReason("");
    setError(null);
  }
  if (!isOpen && seededFor !== null) {
    setSeededFor(null);
  }

  const handleConfirm = async () => {
    setError(null);
    try {
      await onConfirm(reason.trim() || undefined);
      onClose();
    } catch (err) {
      if (isConflictError(err)) {
        setError(ALREADY_DECIDED_COPY);
        return;
      }
      setError(
        err instanceof Error ? err.message : "Failed to reject variation.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        variation ? `Reject ${variation.number}?` : "Reject variation"
      }
    >
      <div className="space-y-4">
        {variation && (
          <>
            <p className="text-body text-slate-600 dark:text-slate-300">
              Rejecting this variation removes it from contract value
              calculations. This action is logged. To resurrect a
              rejected variation later, create a new one.
            </p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md bg-slate-50 dark:bg-slate-800/60 p-3">
              <div className="sm:col-span-2">
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Variation
                </dt>
                <dd className="text-body text-slate-900 dark:text-slate-100">
                  {variation.name}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Value impact
                </dt>
                <dd className="mt-0.5">
                  <ValueImpactCell
                    value={variation.valueImpact ?? null}
                    gstApplies={gstApplies}
                    testId="reject-dialog-value"
                  />
                </dd>
              </div>
              <div>
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Effective date
                </dt>
                <dd className="text-body text-slate-900 dark:text-slate-100">
                  {new Date(
                    `${variation.effectiveDate}T00:00:00`,
                  ).toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Varies
                </dt>
                <dd className="mt-0.5">
                  <VariesCell
                    targets={readTargets(variation)}
                    sowsById={sowsById}
                  />
                </dd>
              </div>
            </dl>
          </>
        )}
        <div>
          <label
            htmlFor="reject-variation-reason"
            className="label"
          >
            Reason (optional)
          </label>
          <textarea
            id="reject-variation-reason"
            className="input min-h-[80px]"
            value={reason}
            maxLength={MAX_REASON}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSubmitting}
          />
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            {reason.length}/{MAX_REASON}
          </p>
        </div>
        {error && <Alert variant="error">{error}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={isSubmitting || !variation}
          >
            Reject variation
          </Button>
        </div>
      </div>
    </Modal>
  );
}
